import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

type PaymentStatus = 'Dekont Bekleniyor' | 'Dekont Geldi' | 'Yönetici Kontrolünde' | 'Onaylandı' | 'Eksik Ödeme' | 'İptal';

interface OdemeRow {
  id: string;
  date: string;
  studentId: string;
  studentName: string;
  teacherId: string;
  teacherName: string;
  amount: number;
  lessonCount: number;
  status: PaymentStatus;
  adminApproved: boolean;
  teacherShare: number;
  platformShare: number;
  description: string;
  receiptImage: string | null;
}

interface SelectOption { id: string; name: string; teacherId?: string; }

const statusColors: Record<string, string> = {
  'Dekont Bekleniyor': 'bg-yellow-100 text-yellow-700',
  'Dekont Geldi': 'bg-blue-100 text-blue-700',
  'Yönetici Kontrolünde': 'bg-purple-100 text-purple-700',
  Onaylandı: 'bg-green-100 text-green-700',
  'Eksik Ödeme': 'bg-red-100 text-red-700',
  İptal: 'bg-red-200 text-red-800',
};

const ALL_STATUSES: PaymentStatus[] = ['Dekont Bekleniyor', 'Dekont Geldi', 'Yönetici Kontrolünde', 'Onaylandı', 'Eksik Ödeme', 'İptal'];

const emptyForm = {
  studentId: '', date: '', amount: '', lessonCount: '1',
  status: 'Dekont Bekleniyor' as PaymentStatus, description: '', file: null as File | null,
};

export default function OdemelerPage() {
  const [data, setData] = useState<OdemeRow[]>([]);
  const [students, setStudents] = useState<SelectOption[]>([]);
  const [teachers, setTeachers] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTeacherId, setFilterTeacherId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    const [odemeRes, ogrenciRes, ogretmenRes] = await Promise.all([
      supabase.from('egtpanel_odemeler').select('*').order('olusturma_tarihi', { ascending: false }),
      supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi, atanan_ogretmen_id').eq('aktif', true),
      supabase.from('egtpanel_ogretmenler').select('id, ad_soyad'),
    ]);

    const ogretmenMap: Record<string, string> = {};
    (ogretmenRes.data ?? []).forEach((t: { id: string; ad_soyad: string }) => { ogretmenMap[t.id] = t.ad_soyad; });

    const ogrenciMap: Record<string, { name: string; teacherId: string }> = {};
    const stuList: SelectOption[] = [];
    (ogrenciRes.data ?? []).forEach((s: { id: string; ogrenci_adi: string; atanan_ogretmen_id: string | null }) => {
      ogrenciMap[s.id] = { name: s.ogrenci_adi, teacherId: s.atanan_ogretmen_id ?? '' };
      stuList.push({ id: s.id, name: s.ogrenci_adi, teacherId: s.atanan_ogretmen_id ?? '' });
    });

    setStudents(stuList);
    setTeachers((ogretmenRes.data ?? []).map((t: { id: string; ad_soyad: string }) => ({ id: t.id, name: t.ad_soyad })));

    if (odemeRes.data) {
      setData(odemeRes.data.map((p) => {
        const stu = ogrenciMap[p.ogrenci_id] ?? { name: '-', teacherId: '' };
        return {
          id: p.id,
          date: p.dekont_tarihi,
          studentId: p.ogrenci_id,
          studentName: stu.name,
          teacherId: p.ogretmen_id,
          teacherName: ogretmenMap[p.ogretmen_id] ?? '-',
          amount: Number(p.tutar),
          lessonCount: p.kac_derslik ?? 1,
          status: (p.odeme_durumu ?? 'Dekont Bekleniyor') as PaymentStatus,
          adminApproved: p.yonetici_onay ?? false,
          teacherShare: p.ogretmen_payi ? Number(p.ogretmen_payi) : Math.round(Number(p.tutar) * (Number(p.hakedis_orani ?? 50) / 100)),
          platformShare: p.platform_payi ? Number(p.platform_payi) : Math.round(Number(p.tutar) * (1 - Number(p.hakedis_orani ?? 50) / 100)),
          description: p.aciklama ?? '',
          receiptImage: p.dekont_gorsel_url ?? null,
        };
      }));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter((d) => {
    const q = search.toLowerCase();
    const matchQ = !q || d.studentName.toLowerCase().includes(q) || d.teacherName.toLowerCase().includes(q);
    const matchStatus = !filterStatus || d.status === filterStatus;
    const matchTeacher = !filterTeacherId || d.teacherId === filterTeacherId;
    return matchQ && matchStatus && matchTeacher;
  });

  const totalApproved = data.filter((d) => d.status === 'Onaylandı').reduce((a, d) => a + d.amount, 0);
  const totalPending = data.filter((d) => d.status !== 'Onaylandı' && d.status !== 'İptal').reduce((a, d) => a + d.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const stu = students.find((s) => s.id === form.studentId);
    const teacherId = stu?.teacherId ?? '';
    const amount = Number(form.amount);
    const rate = 50;
    const teacherShare = Math.round(amount * (rate / 100));
    const platformShare = amount - teacherShare;

    if (!teacherId) {
      showToast('Bu öğrenciye atanmış öğretmen yok!');
      setSaving(false);
      return;
    }

    const { error } = await supabase.from('egtpanel_odemeler').insert({
      ogrenci_id: form.studentId,
      ogretmen_id: teacherId,
      dekont_tarihi: form.date,
      tutar: amount,
      kac_derslik: Number(form.lessonCount),
      aciklama: form.description || null,
      odeme_durumu: form.status,
      hakedis_orani: rate,
      ogretmen_payi: teacherShare,
      platform_payi: platformShare,
      yonetici_onay: false,
    });

    setSaving(false);
    if (error) {
      showToast('Hata: Dekont kaydedilemedi.');
    } else {
      showToast('Dekont başarıyla eklendi!');
      setShowModal(false);
      setForm(emptyForm);
      fetchData();
    }
  };

  const [deletePayment, setDeletePayment] = useState<OdemeRow | null>(null);
  const [editStatusPayment, setEditStatusPayment] = useState<OdemeRow | null>(null);

  const handleApprove = async (id: string) => {
    const { error } = await supabase
      .from('egtpanel_odemeler')
      .update({ odeme_durumu: 'Onaylandı', yonetici_onay: true, yonetici_onay_tarihi: new Date().toISOString(), guncelleme_tarihi: new Date().toISOString() })
      .eq('id', id);
    if (!error) {
      setData((prev) => prev.map((d) => d.id === id ? { ...d, status: 'Onaylandı' as PaymentStatus, adminApproved: true } : d));
      showToast('Ödeme onaylandı.');
    }
  };

  const handleDelete = async () => {
    if (!deletePayment) return;
    const { error } = await supabase.from('egtpanel_odemeler').delete().eq('id', deletePayment.id);
    if (!error) { showToast('Ödeme silindi.'); setDeletePayment(null); fetchData(); }
    else { showToast('Hata: Silinemedi.'); }
  };

  const handleStatusUpdate = async (id: string, newStatus: PaymentStatus) => {
    const approved = newStatus === 'Onaylandı';
    await supabase.from('egtpanel_odemeler').update({
      odeme_durumu: newStatus, yonetici_onay: approved,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', id);
    setData((prev) => prev.map((d) => d.id === id ? { ...d, status: newStatus, adminApproved: approved } : d));
    setEditStatusPayment(null);
    showToast('Durum güncellendi.');
  };

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Ödeme & Dekontlar</h1>
          <p className="text-slate-500 text-sm mt-1">Öğrenci ödemelerini ve dekontları yönetin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Dekont Gir
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Tahsilat', val: `₺${totalApproved.toLocaleString('tr-TR')}`, color: 'text-green-700', bg: 'bg-green-50', icon: 'ri-money-dollar-circle-line' },
          { label: 'Bekleyen', val: `₺${totalPending.toLocaleString('tr-TR')}`, color: 'text-amber-700', bg: 'bg-amber-50', icon: 'ri-time-line' },
          { label: 'Dekont Bekliyor', val: data.filter((d) => d.status === 'Dekont Bekleniyor').length, color: 'text-red-700', bg: 'bg-red-50', icon: 'ri-file-warning-line' },
          { label: 'Kontrol Bekliyor', val: data.filter((d) => d.status === 'Yönetici Kontrolünde').length, color: 'text-purple-700', bg: 'bg-purple-50', icon: 'ri-eye-line' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl">
              <i className={`${s.icon} ${s.color}`} />
            </div>
            <div>
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Öğrenci veya öğretmen ara..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Durumlar</option>
            {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filterTeacherId} onChange={(e) => setFilterTeacherId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Öğretmenler</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Tarih', 'Öğrenci', 'Öğretmen', 'Tutar', 'Ders', 'Öğretmen Payı', 'Platform Payı', 'Durum', 'İşlem'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-16">
                    <i className="ri-money-dollar-circle-line text-4xl text-slate-200 block mb-2" />
                    <p className="text-slate-400 text-sm">{data.length === 0 ? 'Henüz dekont girilmemiş' : 'Kayıt bulunamadı'}</p>
                  </td></tr>
                ) : filtered.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.date}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{d.studentName}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.teacherName}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">₺{d.amount.toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.lessonCount} ders</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">₺{d.teacherShare.toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">₺{d.platformShare.toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[d.status] ?? 'bg-gray-100 text-gray-600'}`}>{d.status}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {d.status !== 'Onaylandı' && d.status !== 'İptal' && (
                          <button onClick={() => handleApprove(d.id)} className="text-green-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-green-200 hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">Onayla</button>
                        )}
                        <button onClick={() => setEditStatusPayment(d)} className="text-slate-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"><i className="ri-edit-line" /></button>
                        <button onClick={() => setDeletePayment(d)} className="text-red-500 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line" /></button>
                        {d.receiptImage && (
                          <button onClick={() => setSelectedFile(d.receiptImage)} className="text-indigo-600 text-xs font-medium px-2 py-1.5 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors cursor-pointer">
                            <i className="ri-file-image-line" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Status Modal */}
      {editStatusPayment && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-800">Durum Düzenle</h2>
              <button onClick={() => setEditStatusPayment(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">{editStatusPayment.studentName} — ₺{editStatusPayment.amount.toLocaleString('tr-TR')}</p>
            <div className="space-y-2">
              {ALL_STATUSES.map((s) => (
                <button key={s} onClick={() => handleStatusUpdate(editStatusPayment.id, s)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors cursor-pointer ${editStatusPayment.status === s ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deletePayment && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4"><i className="ri-delete-bin-line text-red-500 text-xl" /></div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Ödemeyi Sil</h3>
            <p className="text-slate-500 text-sm mb-6"><strong>{deletePayment.studentName}</strong> - ₺{deletePayment.amount.toLocaleString('tr-TR')} ödemesini silmek istediğinizden emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeletePayment(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 cursor-pointer">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Dekont Girişi</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Seç *</label>
                <select required value={form.studentId} onChange={(e) => setForm((p) => ({ ...p, studentId: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                  <option value="">— Seçiniz —</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {form.studentId && (
                <div className="bg-indigo-50 rounded-lg p-3 text-xs text-indigo-700">
                  <i className="ri-user-star-line mr-1" />
                  Öğretmen: <strong>{teachers.find((t) => t.id === students.find((s) => s.id === form.studentId)?.teacherId)?.name ?? 'Atanmamış'}</strong>
                  {' · Hak ediş: '}
                  <strong>%50</strong>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Dekont Tarihi *</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tutar (₺) *</label>
                  <input required type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Kaç Derslik?</label>
                  <input type="number" min={1} value={form.lessonCount} onChange={(e) => setForm((p) => ({ ...p, lessonCount: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ödeme Durumu</label>
                  <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as PaymentStatus }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama</label>
                <input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Dekont Görseli</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-slate-300 transition-colors">
                  <input type="file" accept="image/*,.pdf" onChange={(e) => setForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} className="hidden" id="receiptFile" />
                  <label htmlFor="receiptFile" className="cursor-pointer">
                    <i className="ri-upload-cloud-line text-3xl text-slate-300 block mb-2" />
                    <p className="text-sm text-slate-400">{form.file ? form.file.name : 'Dekont görselini yükleyin'}</p>
                    <p className="text-xs text-slate-300 mt-1">PNG, JPG veya PDF</p>
                  </label>
                </div>
              </div>
              {form.amount && (
                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 grid grid-cols-2 gap-2">
                  <div>Öğretmen Payı (%50): <strong className="text-slate-800">₺{Math.round(Number(form.amount) * 0.5).toLocaleString('tr-TR')}</strong></div>
                  <div>Platform Payı (%50): <strong className="text-slate-800">₺{Math.round(Number(form.amount) * 0.5).toLocaleString('tr-TR')}</strong></div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedFile && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
            <i className="ri-file-image-line text-5xl text-slate-300 block mb-3" />
            <p className="text-slate-700 font-medium mb-1">{selectedFile}</p>
            <button onClick={() => setSelectedFile(null)} className="px-6 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer">Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
}